import { type FC, type HTMLAttributes, type ReactElement, useCallback, useState } from 'react'
import { type BackdropProps, Drawer } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { DRAWER_TRANSITION_DURATION, type ModalControls, type ModalNavControls } from './Modal.types'
import { ModalControlsProvider, type ModalPrivateContextState, ModalProvider } from './ModalContext'
import * as styles from './styles'

export type ModalProps = {
  isBackIconDisplayed?: boolean
  isOpen: boolean
  size?: 'small' | 'large'
  modalNavControls?: ModalNavControls
  dynamicPage?: ReactElement
  onBackIconClick?: VoidFunction
  onClose: VoidFunction
}

const Modal: FC<ModalProps & HTMLAttributes<HTMLDivElement>> = ({
  children,
  dynamicPage,
  isBackIconDisplayed,
  isOpen,
  size = 'small',
  modalNavControls = { goToModalPage: () => {}, goToParentPage: () => {}, goToDynamicModalPage: () => {} },
  onBackIconClick,
  onClose,
  ...rest
}) => {
  const theme = useTheme()
  const [isBackdropFilterDisabled, setIsBackdropFilterDisabled] = useState(false)

  const closeModal = useCallback(() => {
    setIsBackdropFilterDisabled(true)
    onClose()
    setTimeout(() => setIsBackdropFilterDisabled(false), DRAWER_TRANSITION_DURATION)
  }, [onClose])

  const modalContextState: ModalPrivateContextState = {
    isBackIconDisplayed,
    onBackIconClick,
    onClose: closeModal,
  }

  const modalControls: ModalControls = { closeModal, ...modalNavControls }

  return (
    <ModalProvider value={modalContextState}>
      <Drawer
        {...rest}
        transitionDuration={DRAWER_TRANSITION_DURATION}
        anchor="right"
        open={isOpen}
        onClose={closeModal}
        role={undefined} // NOTE: To override role='presentation'
        slotProps={{
          paper: {
            role: 'dialog',
            style: styles.paper,
            'aria-labelledby': 'modal-header-title',
          },
          backdrop: {
            'data-test': 'modal-backdrop',
            // NOTE: backdropFilter has to be deactivated before the closing animation due to a Chrome visual bug https://jira.vsct.fr/browse/IVTS-46880
            ...(isBackdropFilterDisabled && { style: { backdropFilter: 'none' } }),
          } as Partial<BackdropProps>, // NOTE: This type assertion is legitimate https://github.com/mui-org/material-ui/issues/20160
        }}
      >
        <ModalControlsProvider value={modalControls}>
          <div css={styles.page(theme, size)}>{dynamicPage || children}</div>
        </ModalControlsProvider>
      </Drawer>
    </ModalProvider>
  )
}

export default Modal
